import { useEffect, useRef } from 'react';
import * as R from 'ramda';
import { GatekeeperAPIStatus, GatewayStatus, TokenIssuanceState, } from '../types';
import { prefixLogger } from '../logger';
const logDebug = prefixLogger('useGetGatekeeperRecord').debug;
const logError = prefixLogger('useGetGatekeeperRecord').error;
export const reducer = (state, action) => {
    switch (action.type) {
        case 'getGatekeeperRecord':
            return Object.assign(Object.assign({}, state), { gatewayStatus: GatewayStatus.CHECKING, gatekeeperRecordState: undefined });
        case 'getGatekeeperRecord_failure':
            return Object.assign(Object.assign({}, state), { gatekeeperRecordState: action.gatekeeperRecord.state });
        case 'getGatekeeperRecord_success':
            return Object.assign(Object.assign({}, state), { gatekeeperRecordState: action.gatekeeperRecord.state, civicPass: Object.assign(Object.assign({}, state.civicPass), { requestPayload: action.gatekeeperRecord.payload }) });
        case 'getGatekeeperRecord_location_not_supported':
        case 'getGatekeeperRecord_issued_location_not_supported':
            return Object.assign(Object.assign({}, state), { gatewayStatus: GatewayStatus.LOCATION_NOT_SUPPORTED, gatekeeperRecordState: action.gatekeeperRecord.state, civicPass: Object.assign(Object.assign({}, state.civicPass), { requestPayload: action.gatekeeperRecord.payload }) });
        case 'getGatekeeperRecord_vpn_not_supported':
        case 'getGatekeeperRecord_issued_vpn_not_supported':
            return Object.assign(Object.assign({}, state), { gatewayStatus: GatewayStatus.VPN_NOT_SUPPORTED, gatekeeperRecordState: action.gatekeeperRecord.state, civicPass: Object.assign(Object.assign({}, state.civicPass), { requestPayload: action.gatekeeperRecord.payload }) });
        case 'getGatekeeperRecord_retries_exhausted':
            return Object.assign(Object.assign({}, state), { gatewayStatus: GatewayStatus.ERROR, gatekeeperRecordState: action.gatekeeperRecord.state });
        case 'getGatekeeperRecord_not_found':
            return Object.assign(Object.assign({}, state), { gatekeeperRecordState: action.gatekeeperRecord.state });
        case 'getGatekeeperRecord_in_partner_review':
            return Object.assign(Object.assign({}, state), { gatewayStatus: GatewayStatus.IN_REVIEW, gatekeeperRecordState: action.gatekeeperRecord.state, tokenIssuanceState: TokenIssuanceState.IN_PARTNER_REVIEW, pending: action.gatekeeperRecord.pending, civicPass: Object.assign(Object.assign({}, state.civicPass), { responsePayload: undefined }) });
        default:
            return state;
    }
};
const useGetGatekeeperRecord = ({ wallet, gatekeeperClient, httpConfig, networkConfig, }, state, dispatch) => {
    const { gatekeeperNetworkAddress } = state;
    const dispatchFetch = () => ({
        type: 'getGatekeeperRecord',
    });
    const dispatchFailure = () => ({
        type: 'getGatekeeperRecord_failure',
        gatekeeperRecord: { state: GatekeeperAPIStatus.SERVER_FAILURE, payload: undefined },
    });
    const dispatchSuccess = (record) => ({
        type: 'getGatekeeperRecord_success',
        gatekeeperRecord: record,
    });
    const dispatchLocationNotSupported = (record) => ({
        type: 'getGatekeeperRecord_location_not_supported',
        gatekeeperRecord: record,
    });
    const dispatchIssuedLocationNotSupported = (record) => ({
        type: 'getGatekeeperRecord_issued_location_not_supported',
        gatekeeperRecord: record,
    });
    const dispatchVpnNotSupported = (record) => ({
        type: 'getGatekeeperRecord_vpn_not_supported',
        gatekeeperRecord: record,
    });
    const dispatchIssuedVpnNotSupported = (record) => ({
        type: 'getGatekeeperRecord_issued_vpn_not_supported',
        gatekeeperRecord: record,
    });
    const dispatchRetriesExhausted = (record) => ({
        type: 'getGatekeeperRecord_retries_exhausted',
        gatekeeperRecord: record,
    });
    const dispatchRecordNotFound = (record) => ({
        type: 'getGatekeeperRecord_not_found',
        gatekeeperRecord: record,
    });
    const dispatchInIssuancePartnerReview = (record) => ({
        type: 'getGatekeeperRecord_in_partner_review',
        gatekeeperRecord: record,
    });
    const getAction = (gatekeeperRecordState) => {
        const actions = {
            [GatekeeperAPIStatus.REQUESTED]: () => dispatchSuccess(gatekeeperRecordState),
            [GatekeeperAPIStatus.ISSUED]: () => dispatchSuccess(gatekeeperRecordState),
            [GatekeeperAPIStatus.ISSUED_EXPIRED]: () => dispatchSuccess(gatekeeperRecordState),
            [GatekeeperAPIStatus.ISSUED_EXPIRY_APPROACHING]: () => dispatchSuccess(gatekeeperRecordState),
            [GatekeeperAPIStatus.ISSUED_LOCATION_NOT_SUPPORTED]: () => dispatchIssuedLocationNotSupported(gatekeeperRecordState),
            [GatekeeperAPIStatus.ISSUED_VPN_NOT_SUPPORTED]: () => dispatchIssuedVpnNotSupported(gatekeeperRecordState),
            [GatekeeperAPIStatus.VPN_NOT_SUPPORTED]: () => dispatchVpnNotSupported(gatekeeperRecordState),
            [GatekeeperAPIStatus.LOCATION_NOT_SUPPORTED]: () => dispatchLocationNotSupported(gatekeeperRecordState),
            [GatekeeperAPIStatus.NOT_REQUESTED]: () => dispatchRecordNotFound(gatekeeperRecordState),
            [GatekeeperAPIStatus.REQUESTED_RETRIES_EXHAUSTED]: () => dispatchRetriesExhausted(gatekeeperRecordState),
        };
        // handle the 'pending' object on the requested response: it's a special subset of 'REQUESTED'
        if (gatekeeperRecordState &&
            (gatekeeperRecordState === null || gatekeeperRecordState === void 0 ? void 0 : gatekeeperRecordState.state) === GatekeeperAPIStatus.REQUESTED &&
            gatekeeperRecordState.pending) {
            return () => dispatchInIssuancePartnerReview(gatekeeperRecordState);
        }
        return actions[gatekeeperRecordState === null || gatekeeperRecordState === void 0 ? void 0 : gatekeeperRecordState.state];
    };
    /**
     * Check to see if there is getGatekeeperRecord and dispatch actions based on the state of the record.
     * If the service call fails dispatch a failure.
     */
    const dispatchGatekeeperRecord = async (abortController) => {
        if (!wallet || !wallet.publicKey || !gatekeeperClient) {
            return;
        }
        logDebug('Fetching Gatekeeper record...', wallet.publicKey);
        dispatch(dispatchFetch());
        try {
            const record = await gatekeeperClient.getGatekeeperRecordWithPayload(wallet.publicKey);
            if ((abortController === null || abortController === void 0 ? void 0 : abortController.signal.aborted) || !record) {
                return;
            }
            logDebug('Gatekeeper record response state: ', GatekeeperAPIStatus[record === null || record === void 0 ? void 0 : record.state]);
            const action = getAction(record);
            if (!action) {
                logError('Cannot dispatch action for invalid Gatekeeper Record State.', { record });
                dispatch(dispatchFailure());
                return;
            }
            dispatch(action());
        }
        catch (error) {
            logError('Failed to fetch Gatekeeper record', error);
            dispatch(dispatchFailure());
        }
    };
    const useHttpConfigRef = (newHttpConfig) => {
        const ref = useRef();
        // We have to perform a deep equality check, otherwise useEffect will run every time the httpConfig object reference changes.
        if (!R.equals(newHttpConfig, ref.current)) {
            ref.current = newHttpConfig;
        }
        return ref.current;
    };
    useEffect(() => {
        const abortController = new AbortController();
        logDebug('useEffect networkConfig.requiresGatekeeperRecordStatusCheck', networkConfig.requiresGatekeeperRecordStatusCheck);
        if (networkConfig.requiresGatekeeperRecordStatusCheck) {
            // if a gateway token exists we should poll as a record should (eventually) exist
            dispatchGatekeeperRecord(abortController);
        }
        return () => abortController.abort();
    }, [
        gatekeeperClient === null || gatekeeperClient === void 0 ? void 0 : gatekeeperClient.initConfig,
        gatekeeperNetworkAddress,
        useHttpConfigRef(httpConfig),
        networkConfig.requiresGatekeeperRecordStatusCheck,
    ]);
    return { dispatchGatekeeperRecord };
};
export default useGetGatekeeperRecord;
